const numberFormatter = new Intl.NumberFormat('he-IL', { maximumFractionDigits: 1 });

const SummaryCard = ({ title, value, note, tone }) => (
  <article className={`panel summary-card metric-card ${tone || ''}`}>
    <span>{title}</span>
    <strong>{numberFormatter.format(value ?? 0)}</strong>
    <small>{note}</small>
  </article>
);

const BarChart = ({ title, items, dataKey, color }) => {
  const max = Math.max(1, ...items.map((item) => item[dataKey] || 0));
  const total = items.reduce((sum, item) => sum + (item[dataKey] || 0), 0);

  return (
    <article className="panel overview-chart">
      <header className="chart-header">
        <div>
          <h2>{title}</h2>
          <span>30 הימים האחרונים</span>
        </div>
        <strong>{numberFormatter.format(total)}</strong>
      </header>
      <div className="chart-scroll">
        <div className="bar-chart" role="img" aria-label={`${title}, 30 הימים האחרונים`}>
          {items.map((item, index) => {
            const height = Math.max(3, ((item[dataKey] || 0) / max) * 100);
            const showLabel = index % 5 === 0 || index === items.length - 1;
            return (
              <div
                className="bar-column"
                key={item.date}
                title={`${item.date}: ${item[dataKey] || 0}`}
              >
                <span className="bar-value">{item[dataKey] || 0}</span>
                <div className="bar-track">
                  <div
                    className="bar-fill"
                    style={{ height: `${height}%`, backgroundColor: color }}
                  />
                </div>
                <small>{showLabel ? item.date.slice(5).replace('-', '/') : ''}</small>
              </div>
            );
          })}
        </div>
      </div>
    </article>
  );
};

const GroupHeader = ({ title, description }) => (
  <header className="overview-group-header">
    <div>
      <h2>{title}</h2>
      <span>{description}</span>
    </div>
  </header>
);

const OverviewPage = ({ overview }) => {
  const metrics = overview.metrics || {};
  const daily = overview.daily || [];

  return (
    <section className="work-area overview-page" dir="rtl">
      <header className="section-header">
        <div>
          <h1>סקירה</h1>
          <span>תמונת מצב של פעילות המערכת</span>
        </div>
      </header>

      <section className="overview-group">
        <GroupHeader title="מקומות" description="מצב מאגר המקומות והאימות" />
        <div className="overview-metrics overview-metrics-places">
          <SummaryCard title="כל המקומות" value={metrics.totalPlaces} note="רשומות במערכת" />
          <SummaryCard
            title="מקומות מאומתים"
            value={metrics.verifiedPlaces}
            note="מידע מנוהל מהמאגר"
            tone="metric-success"
          />
          <SummaryCard
            title="מקומות לא מאומתים"
            value={metrics.unverifiedPlaces}
            note="מידע מתקבל מ-Google"
            tone="metric-warning"
          />
        </div>
      </section>

      <section className="overview-group">
        <GroupHeader title="משתמשים" description="היקף המשתמשים והפעילות האחרונה" />
        <div className="overview-metrics overview-metrics-users">
          <SummaryCard
            title="סה״כ משתמשים"
            value={metrics.totalUsers}
            note="משתמשים רשומים במערכת"
          />
          <SummaryCard
            title="משתמשים פעילים"
            value={metrics.activeUsers}
            note="ייחודיים ב-24 השעות האחרונות"
            tone="metric-info"
          />
        </div>
        <div className="overview-charts overview-single-chart">
          <BarChart title="משתמשים פעילים" items={daily} dataKey="activeUsers" color="#2e90fa" />
        </div>
      </section>

      <section className="overview-group">
        <GroupHeader title="חיפושים וניווט" description="חיפוש מקומות והתחלת דרך ליעד" />
        <div className="overview-metrics overview-metrics-searches">
          <SummaryCard title="חיפושים היום" value={metrics.searchesToday} note="חיפושי מקומות" />
          <SummaryCard
            title="לחיצות בדרך לשם"
            value={metrics.navigationStartsToday}
            note="היום"
            tone="metric-info"
          />
        </div>
        <div className="overview-charts">
          <BarChart title="חיפושים" items={daily} dataKey="searches" color="#0f766e" />
          <BarChart title="בדרך לשם" items={daily} dataKey="navigations" color="#2563eb" />
        </div>
      </section>

      <section className="overview-group">
        <GroupHeader title="דיווחים" description="דיווחי עומס לפי מקור" />
        <div className="overview-metrics overview-metrics-reports">
          <SummaryCard
            title="דיווחי AI היום"
            value={metrics.aiReportsToday}
            note="הערכות שנוצרו אוטומטית"
          />
          <SummaryCard
            title="דיווחי GPS היום"
            value={metrics.gpsReportsToday}
            note="דיווחים מבוססי מיקום"
            tone="metric-info"
          />
          <SummaryCard
            title="דיווחי משתמש היום"
            value={metrics.userReportsToday}
            note="דיווחים שנשלחו מהאפליקציה"
            tone="metric-success"
          />
          <SummaryCard
            title="ממוצע דיווחים יומי"
            value={metrics.averageDailyReports}
            note="ממוצע ב-30 יום"
          />
        </div>
        <div className="overview-charts overview-report-charts">
          <BarChart title="דיווחי AI" items={daily} dataKey="aiReports" color="#b54708" />
          <BarChart title="דיווחי GPS" items={daily} dataKey="gpsReports" color="#2563eb" />
          <BarChart title="דיווחי משתמש" items={daily} dataKey="userReports" color="#12b76a" />
        </div>
      </section>
    </section>
  );
};

export default OverviewPage;
